<template>

  <div class="container">

    <div class="auth-bg"></div>

    <div id="auth-row" class="row justify-content-center align-items-center h-100">

      <ComponentLoader :is-loading="this.isLoading"></ComponentLoader>

      <div class="auth-col col-10 justify-content-center align-items-center">


        <!-- Header -->
        <div class="auth-header d-flex flex-column p-4 pt-6">
          <!-- Logo -->
          <div class="auth-logo d-flex flex-fill justify-content-center pb-2">
            <img
              src="@/assets/images/wavefire-horiz-logo-sm.png"
              alt="WaveFire Compliance"
              style="height: 65px; width: auto;"
            />
          </div>
          <!-- Header Text -->
          <div class="auth-title d-flex justify-content-center">
            <h5 class="text-gray">Login to your account</h5>
          </div>
        </div>

        <!-- Body -->
        <div class="auth-body d-flex justify-content-center align-items-stretch p-4 pb-6">
          <LoginForm
            :step="this.step"
            :authData="authData"
            :authProviders="authProviders"
            :selectedAuthProvider="selectedAuthProvider"
            @reset-form="resetForm"
            @update-step="updateStep"
            @set-loading-state="setIsLoading"
            @submit-user-email="getUserAuthenticationProviders"
            @select-auth-provider="selectAuthProvider"
            @clear-selected-auth-provider="clearSelectedAuthProvider"
            @reset-auth-provider-selection="resetAuthProviderSelection"
            @submit-password-login="attemptLoginWithPassword"
          ></LoginForm>
        </div>

      </div>

    </div>


  </div>

</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex'
import LoginForm from '@/pages/login-v2/LoginForm.vue'
import { PublicClientApplication } from "@azure/msal-browser"
import Axios from "axios";
import ComponentLoader from "../../components/loaders/ComponentLoader.vue"
import { useVuelidate } from '@vuelidate/core'
import { helpers, required, email } from '@vuelidate/validators'

export default {
  name: "Login-v2",
  components: {
    ComponentLoader,
    LoginForm
  },

  data() {
    return {
      title: "Login",
      isLoading: false,
      step: 1,
      rememberMe: false,
      password: null,
      token: null,
      refresh_token: null,
      authData: {
        email: null
      },
      authProviders: [],
      selectedAuthProvider: null,
      msal: {
        instance: null,
        config: null,
      }
    }
  },

  computed: {
    authProviderName: function () {
      if (this.lodash.has(this.selectedAuthProvider, 'provider')) {
        return this.lodash.get(this.selectedAuthProvider, 'provider');
      }
      return null;
    },
    authProviderDomain: function() {
      if (this.lodash.has(this.selectedAuthProvider, 'domain')) {
        return this.lodash.get(this.selectedAuthProvider, 'domain');
      }
    },
    authProviderTenantId: function() {
      if (this.lodash.has(this.selectedAuthProvider, 'tenant_id')) {
        return this.lodash.get(this.selectedAuthProvider, 'tenant_id');
      }
    },
    authProviderClientId: function() {
      if (this.lodash.has(this.selectedAuthProvider, 'client_id')) {
        return this.lodash.get(this.selectedAuthProvider, 'client_id');
      }
    },
  },

  setup () {
    return { v$: useVuelidate() }
  },

  validations () {
    return {
      authData: {
        email: {
          // required,
          email
        }
      }
    }
  },

  beforeMount() {
    //
  },
  mounted() {

    // Check if impersonation token is present in query string
    if (!this.lodash.isNil(this.$route.query.imptoken)) {
      this.attemptImpersonation()
    }

    // Check if token is present in query string
    if (!this.lodash.isNil(this.$route.query.token)) {
      this.attemptTokenizedAuthentication()
    }

  },
  methods: {

    ...mapActions({
      loading: 'app/loading',
      loaded: 'app/loaded',
      doLogin: 'auth/login',
      set: 'auth/set',
    }),

    resetForm() {
      this.isLoading = true;

      this.authData.email = '';
      this.authProviders = [];
      this.selectedAuthProvider = null;
      this.step = 1;
      this.isLoading = false;
    },

    setIsLoading(value) {
      this.isLoading = value;
    },

    updateStep(newStep) {
      this.loading()
      this.step = newStep
      this.loaded()
    },

    async getUserAuthenticationProviders(email) {

      this.authData.email = email

      var self = this
      self.isLoading = true;

      Axios({
        method:'post',
        url:'/login/providers',
        baseURL: import.meta.env.VITE_APP_API_AUTH_URL,
        data: {
          email: email
        }
      })
        .then(res => {
          this.authData.email = res.data.email;
          this.authProviders  = res.data.providers;
          // Auto-select auth provider if only one choice is available
          if (this.authProviders.length == 1) {
            this.selectedAuthProvider = this.authProviders[0];
          }
          this.updateStep(2);
          self.isLoading = false;
        })
        .catch(error => {
          if (this.lodash.has(error, "response.data.error")) {

            let message = this.lodash.get(error, "response.data.message", null);
            console.log("---- Logging message")
            console.log(message)

            self.$toast.error(error.response.data.error)
            self.isLoading = false;
            return;
          } else {
            self.$toast.error("Something went wrong")
            self.isLoading = false;
            return;
          }

        });
    },

    selectAuthProvider(selectedProvider) {
      var self = this
      self.isLoading = true;

      if (selectedProvider.provider == "MICROSOFT_ENTRA_IDP") {
        this.selectedAuthProvider = selectedProvider;
        return this.attemptLoginWithMSAL();
      } else {
        this.selectedAuthProvider = selectedProvider;
        self.isLoading = false;
      }
    },

    clearSelectedAuthProvider() {
      this.selectedAuthProvider = null;
    },

    resetAuthProviderSelection() {
      this.isLoading = true;
      this.selectedAuthProvider = null
      this.isLoading = false;
    },

    async attemptTokenizedAuthentication() {
      await this.$store.dispatch('auth/attemptTokenLogin', {token: this.$route.query.token})
        .then((authData) => {
          this.doLogin({
            access_token: authData.access_token,
            refresh_token: null,
            remember: false,
            expires_in: authData.expires_in,
          })
        })
    },

    async attemptImpersonation() {
      await this.$store.dispatch('auth/attemptImpersonationLogin', {imptoken: this.$route.query.imptoken})
        .then((authData) => {
          this.$store.dispatch('auth/setImpersonationData', authData)
            .then(() => {
              this.doLogin({
                access_token: authData.access_token,
                refresh_token: null,
                remember: false,
                expires_in: authData.expires_in,
              })
            })
        }).catch((err) => {
          console.log("---- LOGGING CAUGHT ERROR ----")
          console.log(err)

        })

    },




    /**
     * Attempt login via password (provider: PASSWORD)
     * @returns {Promise<boolean>}
     */
    async attemptLoginWithPassword(payload) {
      var self = this
      self.isLoading = true;


      Axios({
        method:'post',
        url:'/login',
        baseURL: import.meta.env.VITE_APP_API_AUTH_URL,
        data: {
          email: this.authData.email,
          password: payload.password
        }
      })
        .then(apiResponse => {
          // Perform local app login
          this.doLogin({
            access_token: apiResponse.data.access_token,
            refresh_token: null,
            remember: false,
            expires_in: apiResponse.data.expires_in,
          })
          return true;
        })
        .catch(error => {

          self.isLoading = false;

          if (error.code == "ERR_BAD_REQUEST") {

            let errorMessage
            if (this.lodash.has(error, 'response.data.message')) {
              errorMessage = this.lodash.get(error, 'response.data.message', 'Invalid username/password combination');
            } else {
              errorMessage = "invalid username/password combination";
            }

            self.$toast.error(errorMessage)
          }
        });
    },


    /**
     * Retrieve an existing MSAL instance or create a new one
     * @returns {Promise<IPublicClientApplication|null>}
     */
    async getMSALInstance() {
      if (this.msal.instance !== null) {
        console.log("Instance is not null - returning instance")
        return this.msal.instance
      } else {
        console.log("Instance is null - initializing MSAL Public Client")
        let instance = await this.initMSALInstance();
        this.msal.instance = instance;
        return instance;
      }
    },

    /**
     * Create a new MSAL instance
     * @returns {Promise<IPublicClientApplication>}
     */
    async initMSALInstance() {
      const msalConfig = {
        auth: {
          clientId: this.authProviderClientId,
          authority: `https://login.microsoftonline.com/${this.authProviderTenantId}`,
          redirectUri: window.location
        }
      };
      const msalInstance = await PublicClientApplication.createPublicClientApplication(msalConfig);


      const callbackId = msalInstance.addEventCallback((message) => {
        console.log("-------- MSAL Event Callback --------")
        console.log(message)
        console.log("====================================================================================================")

        if (message.eventType === "msal:loginFailure") {
          this.$toast.error("Something went wrong - please try again or contact an administrator.")
          this.resetAuthProviderSelection();
        }

        // if (message.eventType == "msal:acquireTokenStart") {}
        // if (message.eventType == "msal:acquireTokenStart") {}
        // if (message.eventType == "msal:popupOpened") {}
        // if (message.eventType == "msal:acquireTokenSuccess") {}

        // "msal:loginStart"
        // "msal:loginSuccess"
        // "msal:loginFailure"
        // "msal:acquireTokenStart"
        // "msal:acquireTokenSuccess"
        // "msal:acquireTokenFailure"
        // "msal:acquireTokenNetworkStart"
        // "msal:ssoSilentStart"
        // "msal:ssoSilentSuccess"
        // "msal:ssoSilentFailure"
        // "msal:handleRedirectStart"
        // "msal:handleRedirectEnd"
        // "msal:logoutStart"
        // "msal:logoutEnd"
        // "msal:logoutSuccess"
        // "msal:logoutFailure"
        // "msal:accountAdded"
        // "msal:accountRemoved"
        // "msal:activeAccountChanged"
        // "msal:initializeStart"
        // "msal:initializeEnd"
        // "msal:popupOpened"
        // "msal:acquireTokenSuccess"

      })



      return msalInstance;
    },

    /**
     * Attempt login via SSO with Microsoft Authentication Library (provider: MICROSOFT_ENTRA_IDP)
     * @returns {Promise<boolean>}
     */
    async attemptLoginWithMSAL() {

      // console.log("authProviderDomain: " + this.authProviderDomain)
      // console.log("authProviderTenantId: " + this.authProviderTenantId)
      // console.log("authProviderClientId: " + this.authProviderClientId)

      const instance = await this.getMSALInstance();

      try {
        const providerResponse = await instance.loginPopup({
          scopes: ["User.Read"],
          loginHint: this.authData.email,
        });
        console.log("---- Logging MSAL Login Provider Response ----")
        console.log(providerResponse)


        // Verify if there are multiple MSAL accounts available
        // need to call getAccount here?
        console.log("---- Checking for multiple MSAL accounts ----")
        const currentAccounts = instance.getAllAccounts();

        console.log(currentAccounts)

        if (!currentAccounts || currentAccounts.length < 1) {
          console.log("currentAccounts: A")
        } else if (currentAccounts.length > 1) {
          // Add choose account code here
          console.log("currentAccounts: B")
        } else if (currentAccounts.length === 1) {
          console.log("currentAccounts: C")
          const activeAccount = currentAccounts[0];
          instance.setActiveAccount(activeAccount);
          // accountId = activeAccount.homeAccountId;
          // showWelcomeMessage(activeAccount);
        }

        return await this.handleMSALPayload(providerResponse);
      } catch (e) {
        // Handle error
        console.log(e)
        this.resetAuthProviderSelection();
      }

      // try {
      //   const silentResponse = await instance.ssoSilent({})
      //   console.log("======== SILENT RESPONSE SUCCESS ========")
      //   console.log(silentResponse)
      //   return this.handleMSALPayload(silentResponse);
      // } catch (e) {
      //   console.log("---- Error during silent login request ----")
      //   try {
      //     const providerResponse = await instance.loginPopup({
      //       scopes: ["User.Read"]
      //     });
      //     console.log("---- Logging Login Response ----")
      //     console.log(providerResponse)
      //     return this.handleMSALPayload(providerResponse);
      //   } catch (e) {
      //     console.log(e)
      //     self.isLoading = false;
      //     // handle error
      //   }
      // }
      console.log("-------- attemptLoginWithMSAL: EOF --------")
      this.resetAuthProviderSelection();
    },


    async handleMSALPayload(providerResponse) {

      console.log("-------- handleMSALPayload --------")
      var self = this;

      Axios({
        method:'post',
        url:'/login/providers/verify',
        baseURL: import.meta.env.VITE_APP_API_AUTH_URL,
        data: {
          access_token: providerResponse.accessToken,
          email: this.authData.email,
          meta: providerResponse,
          provider: this.authProviderName,
        }
      })
        .then(apiResponse => {
          self.isLoading = false;
          try {
            // Perform local app login
            this.doLogin({
              access_token: apiResponse.data.access_token,
              refresh_token: null,
              remember: false,
              expires_in: apiResponse.data.expires_in,
            })
          } catch(error) {
            console.log("======== Error During DoLogin Request ========")
            console.log(error)
            self.isLoading = false;
          };
        })
        .catch(error => {
          console.error(error)
          this.$toast.error(error.response.data.message)
          this.resetAuthProviderSelection();
          self.isLoading = false;
        });

    }



  },
}
</script>

<style>

.auth-bg {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 0;
  height: 100%;
  width: 100%;
  z-index: -1;
  background: #f0f4f9;
}

.auth-col {
  background: white;
  border-radius: 15px;
}

.auth-header {

}

.auth-logo {
  min-width: 200px;
}

form.auth-form {
  min-width: 350px;
}

.auth-button-divider {
  height: 1px;
  background: #b7b7b7;
  width: 100%;
}

button.auth-sso-selection i {
  margin-right: 10px;
  padding-right: 10px;
  border-right: 1px solid white;
}

</style>
